import { ReactNode, useEffect, useRef } from "react";

import { Breadcrumb, type BreadcrumbItem } from "../Breadcrumb/Breadcrumb";
import styles from "./PageHeader.module.css";

interface Props {
  breadcrumb?: BreadcrumbItem[];
  /** Omit on pages that carry their own h1 below the header (see
   *  LeaderboardDetailPage's detail head) — the crumbs then name the page. */
  title?: ReactNode;
  /** A muted line under the title. */
  subtitle?: ReactNode;
  actions?: ReactNode;
  tabs?: ReactNode;
}

/**
 * Sticky frosted page header — breadcrumb + h1 + actions + optional tabs row.
 * Mirrors shell-v2.html L173–L189: position sticky, --bg-header backdrop blur,
 * border-bottom. The host page is expected to scroll inside the main column
 * so the header pins to the top of the visible viewport.
 */
export function PageHeader({
  breadcrumb,
  title,
  subtitle,
  actions,
  tabs,
}: Props) {
  // Published on the scroll column as --page-header-h, so anything else that
  // sticks in it can stick just below the header rather than under it.
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    const host = el?.parentElement;
    if (!el || !host || typeof ResizeObserver === "undefined") return;
    const publish = () =>
      host.style.setProperty("--page-header-h", `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      host.style.removeProperty("--page-header-h");
    };
  }, []);

  return (
    <header ref={ref} className={styles.pageHeader}>
      <div className={styles.row}>
        <div className={styles.left}>
          {breadcrumb && <Breadcrumb items={breadcrumb} />}
          {title !== undefined && <h1 className={styles.title}>{title}</h1>}
          {subtitle !== undefined && (
            <div className={styles.subtitle}>{subtitle}</div>
          )}
        </div>
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
      {tabs && <div className={styles.tabsRow}>{tabs}</div>}
    </header>
  );
}
